import * as React from 'react';
import { map } from 'es-toolkit/compat';

import { ExpandableParagraph, Typography } from 'omakase';
import { Grid } from 'ui';
import { core } from '@tock/coretypes';
import {
  generateOptionGroupOptionProps,
  generateOptionGroupProps,
  helpTextFromTicketOptionGroup,
  isRequiredOptionGroup,
} from '@tock/shared/utils/option';

import { Theme, css } from '@emotion/react';
import { useAddonsPriceDisplayDetails } from 'utils/priceDisplayUtils';
import ModifierCompact from 'components/business/addons/ModifierCompact';

const rootStyles = css`
  width: 100%;
`;

const headingStyles = ({ spacing, colors }: Theme) => css`
  border-bottom: 1px solid ${colors.borderDefault};
  margin-bottom: ${spacing(2)}px;
`;

const headingSubStyles = css`
  display: flex;
  flex: 1 0 auto;
  justify-content: space-between;
`;

interface Props {
  ticketType: core.ITicketType;
  ticketOptionGroup: core.ITicketOptionGroup;
  ticketOptions: core.ITicketOption[];
  optionOrders: core.IOptionOrder[];
  ticketOptionPrices: core.IPrice[];
  isOpen: boolean;
  partySize: number;
  isSkipped: boolean;
  isCompleted: boolean;
  onChangeQuantity(selectedOptions: core.IOptionOrder[]): void;
  patron?: core.IPatron;
}

const ModifierGroupCompact: React.FunctionComponent<Props> = ({
  ticketType,
  ticketOptionGroup,
  ticketOptions,
  optionOrders,
  ticketOptionPrices,
  partySize,
  onChangeQuantity,
  patron,
}) => {
  const { sortedTicketOptions, numSelectedInGroup, handleChangeQuantity } =
    generateOptionGroupProps({
      optionOrders,
      ticketOptions,
      onChangeQuantity,
      patron,
      ticketOptionGroup,
    });

  const helpText = helpTextFromTicketOptionGroup(partySize, ticketOptionGroup);

  const { useInclusivePricing, feeDisclaimerLabel, showFeeDisclaimerLabel } =
    useAddonsPriceDisplayDetails(ticketType);

  const supplements = map(sortedTicketOptions, (ticketOption) => {
    const ticketOptionId = ticketOption.id as number;
    const {
      minRequiredIncrement,
      maxQuantity,
      price,
      numSelectedOfSupplement,
      isExcluded,
      isSoldOut,
      numFiredToKitchen,
    } = generateOptionGroupOptionProps({
      ticketOption,
      ticketOptionGroup,
      ticketOptions,
      partySize,
      numSelectedInGroup,
      ticketOptionPrices,
      optionOrders,
      optionExclusion: ticketType.optionExclusion ?? [],
      useInclusivePricing,
    });

    return (
      <ModifierCompact
        description={ticketOption.description ?? ''}
        disabled={isExcluded || isSoldOut}
        isSoldOut={isSoldOut}
        id={ticketOptionId}
        key={ticketOptionId}
        name={ticketOption.name ?? ''}
        minRequiredIncrement={minRequiredIncrement}
        maxQuantity={maxQuantity}
        price={price}
        quantity={numSelectedOfSupplement}
        onChangeQuantity={handleChangeQuantity}
        numFiredToKitchen={numFiredToKitchen}
      />
    );
  });

  return (
    <div css={rootStyles}>
      <div css={headingStyles}>
        <Typography component="h2" variant="body01Strong">
          {ticketOptionGroup.name}
        </Typography>
        {ticketOptionGroup.description && (
          <ExpandableParagraph
            paragraphText={ticketOptionGroup.description}
            linkify={true}
            cutoffLength={255}
            mb={1}
          />
        )}

        <Typography
          css={headingSubStyles}
          variant="body02"
          color="fgSubtle"
          component="div"
          mb={1.5}
        >
          <p>{!!helpText && helpText}</p>
          <p>{isRequiredOptionGroup(ticketOptionGroup) ? 'Required' : 'Optional'}</p>
        </Typography>
      </div>

      <Grid container spacing={2} data-testid="supplementGroup-supplements">
        {supplements}
      </Grid>

      {showFeeDisclaimerLabel && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="fgSubtle">
              {feeDisclaimerLabel}
            </Typography>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ModifierGroupCompact;
